import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faChartPie,
  faChartLine,
  faMoneyBillWave,
  faArrowTrendUp,
  faUsers,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const formatCurrency = (n) => `TZS ${Number(n).toLocaleString()}`;
const formatK = (n) =>
  n >= 1000000
    ? `${(n / 1000000).toFixed(1)}M`
    : n >= 1000
      ? `${(n / 1000).toFixed(0)}K`
      : n;

// ─── DEMO DATA ──────────────────────────────────────────────────
const MONTHLY_SALES_DATA = [
  { month: "Jan", sales: 3500000, profit: 980000, cost: 2520000, orders: 45 },
  { month: "Feb", sales: 4200000, profit: 1176000, cost: 3024000, orders: 52 },
  { month: "Mar", sales: 3900000, profit: 1092000, cost: 2808000, orders: 48 },
  { month: "Apr", sales: 4700000, profit: 1316000, cost: 3384000, orders: 58 },
  { month: "May", sales: 5100000, profit: 1428000, cost: 3672000, orders: 62 },
  { month: "Jun", sales: 4800000, profit: 1344000, cost: 3456000, orders: 55 },
  { month: "Jul", sales: 5300000, profit: 1484000, cost: 3816000, orders: 65 },
  { month: "Aug", sales: 4900000, profit: 1372000, cost: 3528000, orders: 58 },
  { month: "Sep", sales: 5500000, profit: 1540000, cost: 3960000, orders: 68 },
  { month: "Oct", sales: 6000000, profit: 1680000, cost: 4320000, orders: 72 },
  { month: "Nov", sales: 5800000, profit: 1624000, cost: 4176000, orders: 70 },
  { month: "Dec", sales: 6500000, profit: 1820000, cost: 4680000, orders: 78 },
];

const CATEGORY_SALES_DATA = [
  { name: "Food", value: 32000000, color: "#0a5cdf" },
  { name: "Detergents", value: 18000000, color: "#10b981" },
  { name: "Oils", value: 15000000, color: "#df920c" },
  { name: "Electronics", value: 12000000, color: "#e02020" },
  { name: "Clothing", value: 8000000, color: "#8b5cf6" },
  { name: "Beverages", value: 6000000, color: "#06b6d4" },
];

const SELLER_PERFORMANCE = [
  {
    seller: "Saida Mussa Bakari",
    sales: 3500000,
    transactions: 42,
    avgPerTransaction: 83333,
  },
  {
    seller: "Ali Hassan Juma",
    sales: 2800000,
    transactions: 35,
    avgPerTransaction: 80000,
  },
  {
    seller: "Fatuma Kondo Said",
    sales: 2450000,
    transactions: 28,
    avgPerTransaction: 87500,
  },
  {
    seller: "Salim Bakari Omar",
    sales: 1800000,
    transactions: 20,
    avgPerTransaction: 90000,
  },
  {
    seller: "Maryam Ali Kondo",
    sales: 1500000,
    transactions: 18,
    avgPerTransaction: 83333,
  },
];

const YEARLY_DATA = {
  2024: {
    monthly: MONTHLY_SALES_DATA.map((d) => ({
      ...d,
      sales: d.sales * 0.8,
      profit: d.profit * 0.8,
      cost: d.cost * 0.8,
      orders: Math.round(d.orders * 0.8),
    })),
    totalSales: 45000000,
    totalProfit: 12600000,
    totalCost: 32400000,
    totalOrders: 520,
  },
  2025: {
    monthly: MONTHLY_SALES_DATA.map((d) => ({
      ...d,
      sales: d.sales * 0.9,
      profit: d.profit * 0.9,
      cost: d.cost * 0.9,
      orders: Math.round(d.orders * 0.9),
    })),
    totalSales: 52000000,
    totalProfit: 14560000,
    totalCost: 37440000,
    totalOrders: 590,
  },
  2026: {
    monthly: MONTHLY_SALES_DATA,
    totalSales: 62200000,
    totalProfit: 17416000,
    totalCost: 44784000,
    totalOrders: 691,
  },
};

// const CHART_COLORS = ["#0a5cdf", "#10b981", "#df920c", "#e02020", "#8b5cf6", "#06b6d4"];

const OverallSalesTab = ({ shop }) => {
  const [selectedYear, setSelectedYear] = useState("2026");
  const [chartType, setChartType] = useState("bar");
  const [viewMode, setViewMode] = useState("overview");

  const years = Object.keys(YEARLY_DATA);
  const currentData = YEARLY_DATA[selectedYear] || YEARLY_DATA["2026"];

  // ─── Custom Tooltip ────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-sm">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((p, i) => (
          <p
            key={i}
            style={{ color: p.color }}
            className="flex items-center gap-2"
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: p.color }}
            ></span>
            {p.name}:{" "}
            {typeof p.value === "number" && p.value > 10000
              ? formatCurrency(p.value)
              : p.value}
          </p>
        ))}
      </div>
    );
  };

  // ─── Stats Cards ──────────────────────────────────────────────
  const StatsCards = () => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Card className="p-4 border border-gray-200 shadow-sm bg-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Total Sales
            </p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {formatCurrency(currentData.totalSales)}
            </p>
          </div>
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-blue-600 h-4 w-4"
            />
          </div>
        </div>
      </Card>

      <Card className="p-4 border border-gray-200 shadow-sm bg-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Total Profit
            </p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {formatCurrency(currentData.totalProfit)}
            </p>
          </div>
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faArrowTrendUp}
              className="text-green-600 h-4 w-4"
            />
          </div>
        </div>
      </Card>

      <Card className="p-4 border border-gray-200 shadow-sm bg-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Total Orders
            </p>
            <p className="text-2xl font-bold text-purple-600 mt-1">
              {currentData.totalOrders}
            </p>
          </div>
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faShoppingCart}
              className="text-purple-600 h-4 w-4"
            />
          </div>
        </div>
      </Card>

      <Card className="p-4 border border-gray-200 shadow-sm bg-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Avg Order Value
            </p>
            <p className="text-2xl font-bold text-orange-600 mt-1">
              {currentData.totalOrders > 0
                ? formatCurrency(
                    Math.round(
                      currentData.totalSales / currentData.totalOrders,
                    ),
                  )
                : formatCurrency(0)}
            </p>
          </div>
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faChartLine}
              className="text-orange-600 h-4 w-4"
            />
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Sales Analytics
          </h3>
          <p className="text-sm text-gray-600">
            Comprehensive overview of your shop's sales performance
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Year Filter */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  selectedYear === year
                    ? "bg-blue-800 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                }`}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Chart Type Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-2">
            <button
              onClick={() => setChartType("bar")}
              className={`p-1.5 rounded-md transition-all duration-200 ${
                chartType === "bar"
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              <FontAwesomeIcon icon={faChartBar} className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setChartType("line")}
              className={`p-1.5 rounded-md transition-all duration-200 ${
                chartType === "line"
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              <FontAwesomeIcon icon={faChartLine} className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Stats Cards ─── */}
      <StatsCards />

      {/* ─── View Mode Tabs ─── */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
        <button
          onClick={() => setViewMode("overview")}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            viewMode === "overview"
              ? "bg-blue-800 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
          }`}
        >
          <FontAwesomeIcon icon={faChartBar} className="mr-2 h-3.5 w-3.5" />
          Overview
        </button>
        <button
          onClick={() => setViewMode("categories")}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            viewMode === "categories"
              ? "bg-blue-800 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
          }`}
        >
          <FontAwesomeIcon icon={faChartPie} className="mr-2 h-3.5 w-3.5" />
          Categories
        </button>
        <button
          onClick={() => setViewMode("sellers")}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            viewMode === "sellers"
              ? "bg-blue-800 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
          }`}
        >
          <FontAwesomeIcon icon={faUsers} className="mr-2 h-3.5 w-3.5" />
          Sellers
        </button>
      </div>

      {/* ─── Overview View ─── */}
      {viewMode === "overview" && (
        <Card className="p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-blue-600 h-3.5 w-3.5"
                />
              </div>
              <h4 className="font-semibold text-gray-900">
                Monthly Sales Trend
              </h4>
            </div>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
              {selectedYear}
            </Badge>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {chartType === "bar" ? (
              <BarChart data={currentData.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={formatK} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="sales"
                  name="Sales"
                  fill="#0a5cdf"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="profit"
                  name="Profit"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="cost"
                  name="Cost"
                  fill="#df920c"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            ) : (
              <LineChart data={currentData.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={formatK} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#0a5cdf"
                  strokeWidth={2}
                  name="Sales"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Profit"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#df920c"
                  strokeWidth={2}
                  name="Cost"
                  dot={{ r: 3 }}
                  strokeDasharray="4 4"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </Card>
      )}

      {/* ─── Categories View ─── */}
      {viewMode === "categories" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faChartPie}
                  className="text-purple-600 h-3.5 w-3.5"
                />
              </div>
              <h4 className="font-semibold text-gray-900">Sales by Category</h4>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={CATEGORY_SALES_DATA}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {CATEGORY_SALES_DATA.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-green-600 h-3.5 w-3.5"
                />
              </div>
              <h4 className="font-semibold text-gray-900">Category Revenue</h4>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={CATEGORY_SALES_DATA} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  type="number"
                  tickFormatter={formatK}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fontSize: 11 }}
                  width={80}
                />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="value" name="Revenue" radius={[0, 4, 4, 0]}>
                  {CATEGORY_SALES_DATA.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* ─── Sellers View ─── */}
      {viewMode === "sellers" && (
        <Card className="p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUsers}
                className="text-indigo-600 h-3.5 w-3.5"
              />
            </div>
            <h4 className="font-semibold text-gray-900">Seller Performance</h4>
            <Badge className="bg-indigo-600 text-white border-indigo-200 ml-2">
              {SELLER_PERFORMANCE.length} sellers
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={SELLER_PERFORMANCE}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="seller" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={formatK} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar
                  dataKey="sales"
                  name="Total Sales"
                  fill="#8b5cf6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>

            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={SELLER_PERFORMANCE}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="seller" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar
                  dataKey="transactions"
                  name="Transactions"
                  fill="#06b6d4"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Seller Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                    Seller Name
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-right">
                    Total Sales
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                    Transactions
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-right">
                    Avg/Transaction
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                    Performance
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {SELLER_PERFORMANCE.map((seller, idx) => {
                  const maxSales = Math.max(
                    ...SELLER_PERFORMANCE.map((s) => s.sales),
                  );
                  const percentage = (seller.sales / maxSales) * 100;
                  return (
                    <TableRow
                      key={idx}
                      className="hover:bg-blue-50/30 transition-colors border-b border-gray-100"
                    >
                      <TableCell className="py-2.5 px-3 text-sm font-medium text-gray-900">
                        {seller.seller}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-sm font-semibold text-green-600 text-right">
                        {formatCurrency(seller.sales)}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-sm text-gray-600 text-center">
                        {seller.transactions}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-sm font-semibold text-blue-600 text-right">
                        {formatCurrency(seller.avgPerTransaction)}
                      </TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default OverallSalesTab;
